import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  boardTableSchema,
  type ApiError,
  type BoardTable,
  type BoardTableRow,
} from '@molt/shared';
import { get } from './api';
import {
  EMPTY_BOARD_FILTER,
  boardFilterCacheKey,
  boardFilterSearchParams,
  type BoardFilterSelection,
} from './board-filter';
import { queryKeys } from './query-keys';

export type BoardTableSortKey = 'card' | 'list' | 'due';
export type BoardTableSortDirection = 'asc' | 'desc';

export interface BoardTableSort {
  readonly key: BoardTableSortKey;
  readonly direction: BoardTableSortDirection;
}

export interface BoardTableMoveNeighbours {
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
}

export function boardTableQueryString(filter: BoardFilterSelection): string {
  return boardFilterSearchParams(filter).toString();
}

export function getBoardTable(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): Promise<BoardTable> {
  const query = boardTableQueryString(filter);
  const suffix = query === '' ? '' : `?${query}`;
  return get(`/boards/${encodeURIComponent(boardId)}/table${suffix}`, boardTableSchema);
}

export function boardTableQueryOptions(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): UseQueryOptions<BoardTable, ApiError> {
  const queryKey = boardFilterCacheKey(filter);
  return {
    queryKey: queryKeys.boards.table(boardId, queryKey),
    queryFn: () => getBoardTable(boardId, filter),
  };
}

export function useBoardTable(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
  enabled = true,
): UseQueryResult<BoardTable, ApiError> {
  return useQuery({ ...boardTableQueryOptions(boardId, filter), enabled });
}

export function nextTableSort(
  current: BoardTableSort | null,
  key: BoardTableSortKey,
): BoardTableSort {
  if (current?.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: 'asc' };
}

/**
 * ⚠ GENERIC over the row, and that is load-bearing rather than tidy. FE-56.2
 * made `BoardTable['rows']` a `BoardTableCustomFieldRow[]` — a
 * `BoardTableRow` plus its custom-field cells — and a signature fixed to
 * `BoardTableRow[]` WIDENS on the way out: `tsc` accepts the call (the wider row
 * is assignable to the narrower parameter) and then hands `BoardTableView` rows
 * whose `customFields` key it can no longer see. The cells would vanish from
 * every sorted render with nothing red anywhere. The type parameter keeps the
 * caller's row shape.
 */
export function sortedBoardTableRows<Row extends BoardTableRow>(
  rows: readonly Row[],
  sort: BoardTableSort | null,
): Row[] {
  const copy = [...rows];
  if (sort === null) return copy;

  const direction = sort.direction === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    const compared = compareRows(a, b, sort.key);
    if (compared !== 0) return compared * direction;
    return compareRows(a, b, 'list') || compareRows(a, b, 'card');
  });
}

export function tableMoveNeighbours(
  rows: readonly BoardTableRow[],
  cardId: string,
  targetListId: string,
  beforeCardId: string | null = null,
): BoardTableMoveNeighbours {
  const targetRows = rows.filter(
    (row) => row.list.id === targetListId && row.card.id !== cardId,
  );
  const beforeIndex =
    beforeCardId === null
      ? targetRows.length
      : targetRows.findIndex((row) => row.card.id === beforeCardId);
  const insertIndex = beforeIndex < 0 ? targetRows.length : beforeIndex;
  return {
    previousCardId: targetRows[insertIndex - 1]?.card.id ?? null,
    nextCardId: targetRows[insertIndex]?.card.id ?? null,
  };
}

function compareRows(a: BoardTableRow, b: BoardTableRow, key: BoardTableSortKey): number {
  if (key === 'card') return compareText(a.card.title, b.card.title);
  if (key === 'list') return compareText(a.list.title, b.list.title);
  return compareNullableDate(a.card.dueAt, b.card.dueAt);
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

function compareNullableDate(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
}
